import React, { useEffect, useState } from 'react';
import { getUsageReport } from 'integration';
import styles from './MeteringView.module.scss';
import {
  IUsageReportResponse,
  IUsageDetail,
  IMonthlyUsageDetail,
  IUsageDetailGroupedByProduct
} from './MeteringView.interface';
import {
  CostConsumedCards,
  BarChartCard,
  LoadingSpinner,
  SavedTimeCards,
  Footer
} from 'app/components';
import { getAccountInfo } from 'app/auth';
import { useAppSelector } from 'app/redux';

export const MeteringView: React.FC = () => {
  const [currency, setCurrency] = useState<string>('');
  const [overallRequests, setOverallRequests] = useState<number>(0);
  const [overallCost, setOverallCost] = useState<number>(0);
  const [overallEstimatedSavedTime, setoverallEstimatedSavedTime] =
    useState<number>(0);
  const [overallEstimatedSavedMoney, setOverallEstimatedSavedMoney] =
    useState<number>(0);

  const [dataGroupedByMonth, setDataGroupedByMonth] = useState<{
    [key: string]: IMonthlyUsageDetail;
  }>({});

  const [loading, setLoading] = useState<boolean>(false);

  const currentYear = new Date().getFullYear();
  const startDate = `${currentYear}-01-01`;
  const endDate = `${currentYear}-12-31`;

  const userInfo = useAppSelector(getAccountInfo);
  const userName = userInfo?.name?.split(' ')[0];

  useEffect(() => {
    setLoading(true);
    getUsageReport(startDate, endDate)
      .then((response) => {
        if (response) {
          onUsageReportResponse(response);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const groupResponseDataByMonth = (data: { detail: IUsageDetail[] }): void => {
    const groupedData: { [key: string]: IMonthlyUsageDetail } = {};
    for (const item of data.detail) {
      const month = item.date.split('-')[1].replace(/^0+/, '');
      if (!groupedData[month]) {
        groupedData[month] = {
          totalAmount: 0,
          totalRequests: 0,
          estimatedSavedMoney: 0,
          estimatedSavedTimeInHours: 0,
          services: {}
        };
      }

      groupedData[month].totalAmount += item.totalAmount;
      groupedData[month].totalRequests += item.totalRequests;
      groupedData[month].estimatedSavedMoney += item.estimatedSavedMoney;
      groupedData[month].estimatedSavedTimeInHours +=
        item.estimatedSavedTimeInHours;

      if (!groupedData[month].services[item.productName]) {
        groupedData[month].services[item.productName] = 0;
      }
      groupedData[month].services[item.productName] += item.totalRequests;
    }

    for (let month = 1; month <= 12; month++) {
      if (!groupedData[month]) {
        groupedData[month] = {
          totalAmount: 0,
          totalRequests: 0,
          estimatedSavedMoney: 0,
          estimatedSavedTimeInHours: 0,
          services: {}
        };
      }
    }
    setDataGroupedByMonth(groupedData);
  };

  const groupResponseDataByProduct = (data: {
    detail: IUsageDetail[];
  }): void => {
    const groupedData: { [key: string]: IUsageDetailGroupedByProduct } = {};
    for (const item of data.detail) {
      const productName = item.productName;
      if (!groupedData[productName]) {
        groupedData[productName] = {
          estimatedSavedMoney: 0,
          estimatedSavedTimeInHours: 0
        };
      }

      groupedData[productName].estimatedSavedMoney += item.estimatedSavedMoney;
      groupedData[productName].estimatedSavedTimeInHours +=
        item.estimatedSavedTimeInHours;
    }
  };

  const onUsageReportResponse = (response: IUsageReportResponse): void => {
    groupResponseDataByMonth(response);
    groupResponseDataByProduct(response);
    setCurrency(response.currency ?? '');
    setOverallRequests(response.overallRequests ?? 0);
    setOverallCost(response.overallCost ?? 0);
    setOverallEstimatedSavedMoney(response.overallEstimatedSavedMoney ?? 0);
    setoverallEstimatedSavedTime(
      response.overallEstimatedSavedTimeInHours ?? 0
    );
  };

  return (
    <>
      <div className={styles['cost-consumed-container']}>
        {dataGroupedByMonth && (
          <div>
            <h3 className={styles.title}>
              {userName != null && (
                <p>Hello {userName}, your consumption is:</p>
              )}
            </h3>
            <div className={styles.content}>
              <div className={styles.cards}>
                <CostConsumedCards
                  currency={currency}
                  overallCost={overallCost}
                  overallRequests={overallRequests}
                  usageData={dataGroupedByMonth}
                />
                <SavedTimeCards
                  currency={currency}
                  overallEstimatedSavedMoney={overallEstimatedSavedMoney}
                  overallEstimatedSavedTimeInHours={overallEstimatedSavedTime}
                  usageData={dataGroupedByMonth}
                />
              </div>
              <div className={styles.chart}>
                <BarChartCard usageData={dataGroupedByMonth} />
              </div>
            </div>
          </div>
        )}
        {loading && <LoadingSpinner />}
      </div>

      <Footer />
    </>
  );
};
